/**
 * LiveStockContext — universal, sub-second, push-driven live stock cache.
 *
 * RC-211 (2026-08-06). Per PERFORMANCE_BIBLE.md:
 *   §1.2 Batch reads       — debounced bulk fetch, one round-trip per burst
 *   §1.5 No stock caching  — server never caches; only the CLIENT holds a
 *                            Map that Pusher invalidates in real time
 *   §2.4 Memoize           — useSyncExternalStore ensures only the badges
 *                            whose JP just changed re-render
 *   §4.3 WebSockets only   — reuses the existing `stock-trail-live` channel
 *
 * Data flow:
 *   1. <StockBadge jpPartNo="X"/> mounts → useLiveStock(X)
 *   2. Hook enqueues X into a global "pending fetch" set
 *   3. 60 ms debounce → single POST /api/parts/live-stock with all
 *      pending JPs → results written into a shared Map
 *   4. Pusher `stock_snapshot` events also write into that Map
 *      → any card listening on that JP re-renders instantly (< 300 ms
 *      after a warehouse scan hits the server)
 *   5. New-JP-first-appearance: since the ledger poller emits a snapshot
 *      for EVERY JP touched in the ledger log, brand-new parts get
 *      broadcast the moment the first receive hits stock_ledger →
 *      the badge appears live without any refresh.
 */

import React, {
  createContext, useContext, useEffect, useRef, useCallback, useSyncExternalStore,
} from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Global mutable store (kept OUTSIDE React state on purpose so
//    multiple <StockBadge> instances share a single Map and a single
//    subscriber set. useSyncExternalStore reads directly from here.)

/** @type {Map<string, {tracked:boolean,total:number,nangli_puna:number,kashmere_gate:number}>} */
const _cache = new Map();

/** Set of listener functions keyed by JP. */
const _listeners = new Map(); // Map<jp, Set<() => void>>

const _pending = new Set();
let _debounceTimer = null;
let _flushInFlight = false;
let _authTokenGetter = () => null;

const _fireListeners = (jp) => {
  const set = _listeners.get(jp);
  if (set) set.forEach((cb) => { try { cb(); } catch { /* ignore */ } });
};

const _upsert = (jp, summary) => {
  _cache.set(jp, summary);
  _fireListeners(jp);
};

const _subscribe = (jp, cb) => {
  let set = _listeners.get(jp);
  if (!set) { set = new Set(); _listeners.set(jp, set); }
  set.add(cb);
  return () => {
    const s = _listeners.get(jp);
    if (s) { s.delete(cb); if (s.size === 0) _listeners.delete(jp); }
  };
};

const _flushPending = async () => {
  if (_flushInFlight) return;
  const jps = Array.from(_pending);
  if (jps.length === 0) return;
  _pending.clear();
  _flushInFlight = true;
  try {
    const token = _authTokenGetter();
    if (!token) { _flushInFlight = false; return; }
    // Split into chunks of 200 per backend cap.
    for (let i = 0; i < jps.length; i += 200) {
      const chunk = jps.slice(i, i + 200);
      const res = await axios.post(
        `${API}/parts/live-stock`,
        { jps: chunk },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 },
      );
      const stocks = res?.data?.stocks || {};
      Object.entries(stocks).forEach(([jp, summary]) => _upsert(jp, summary));
    }
  } catch {
    // Silent — badge falls back to "nothing" until next attempt.
  } finally {
    _flushInFlight = false;
    // If more items queued during the fetch, flush again.
    if (_pending.size > 0) {
      clearTimeout(_debounceTimer);
      _debounceTimer = setTimeout(_flushPending, 60);
    }
  }
};

const _requestFetch = (jp) => {
  if (!jp || _cache.has(jp)) return;
  _pending.add(jp);
  clearTimeout(_debounceTimer);
  _debounceTimer = setTimeout(_flushPending, 60);
};

// ─── React Context (mostly just for provider mounting; the data
//     itself lives in the module-level Map above so components can
//     subscribe surgically via useSyncExternalStore.)

const LiveStockContext = createContext(null);

export const LiveStockProvider = ({ children }) => {
  const { token } = useAuth();
  const pusherRef = useRef(null);

  // Keep the token getter in sync so the module-level fetcher can
  // build Authorization headers without importing AuthContext.
  useEffect(() => {
    _authTokenGetter = () => token;
    if (token) {
      // Retry any pending JPs that failed for lack of a token.
      if (_pending.size > 0) {
        clearTimeout(_debounceTimer);
        _debounceTimer = setTimeout(_flushPending, 60);
      }
    }
  }, [token]);

  // Subscribe to Pusher stock-trail-live channel and route
  // `stock_snapshot` events into the shared cache.
  useEffect(() => {
    if (!token) return undefined;
    let pusher;
    let ch;
    let cancelled = false;
    (async () => {
      try {
        const Pusher = (await import('pusher-js')).default;
        const { envChannel } = await import('../utils/pusherEnv');
        if (cancelled) return;
        pusher = new Pusher('cfc00fbbcf3d28bd5df1', { cluster: 'ap2' });
        pusherRef.current = pusher;
        ch = pusher.subscribe(envChannel('stock-trail-live'));
        ch.bind('stock_snapshot', (payload) => {
          if (!payload || !payload.jp_part_no) return;
          _upsert(payload.jp_part_no, {
            tracked: !!payload.tracked,
            total: Number(payload.total || 0),
            nangli_puna: Number(payload.nangli_puna || 0),
            kashmere_gate: Number(payload.kashmere_gate || 0),
          });
        });
      } catch { /* pusher optional — fetch fallback still works */ }
    })();
    return () => {
      cancelled = true;
      try { pusher && pusher.disconnect(); } catch { /* noop */ }
      pusherRef.current = null;
    };
  }, [token]);

  return (
    <LiveStockContext.Provider value={{}}>
      {children}
    </LiveStockContext.Provider>
  );
};

/**
 * Read-side hook. Returns the current summary for a JP or `undefined`
 * until the first fetch resolves. Re-renders ONLY when this JP's
 * cache entry changes (via useSyncExternalStore).
 */
export const useLiveStock = (jpPartNo) => {
  const subscribe = useCallback(
    (cb) => (jpPartNo ? _subscribe(jpPartNo, cb) : () => {}),
    [jpPartNo],
  );
  const getSnapshot = useCallback(
    () => (jpPartNo ? _cache.get(jpPartNo) : undefined),
    [jpPartNo],
  );
  // Kick off a fetch on first mount for this JP.
  useEffect(() => {
    if (jpPartNo) _requestFetch(jpPartNo);
  }, [jpPartNo]);
  return useSyncExternalStore(subscribe, getSnapshot, () => undefined);
};

/** Utility for imperative code (e.g. modals) to preload a batch. */
export const preloadLiveStock = (jps) => {
  (jps || []).forEach((j) => _requestFetch(j));
};

export const useLiveStockContext = () => useContext(LiveStockContext);

export default LiveStockContext;
